import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardSubtitle,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonItem,
    IonLabel,
    IonList,
    IonMenuButton,
    IonNote,
    IonPage,
    IonText,
    IonTitle,
    IonToolbar,
} from '@ionic/react';
import {applyUpdate} from '../../serviceWorkerRegistration';
import {formatReleaseTimestamp, useAppRelease} from '../../services/appRelease';
import './UpdatesPage.css';

const getServiceWorkerStatusLabel = (registration: ServiceWorkerRegistration | null): string => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return 'Service worker wordt niet ondersteund in deze browser.';
    }

    if (!registration) {
        return 'Nog geen service worker registratie gevonden voor deze sessie.';
    }

    if (registration.waiting) {
        return 'Nieuwe versie staat klaar om te activeren.';
    }

    if (registration.installing) {
        return 'Nieuwe versie wordt nu voorbereid.';
    }

    if (registration.active) {
        return 'Service worker actief en gekoppeld aan deze app.';
    }

    return 'Service worker status onbekend.';
};

const UpdatesPage: React.FC = () => {
    const {
        installedRelease,
        latestRelease,
        isChecking,
        isUpdateAvailable,
        lastCheckedAt,
        refreshReleaseInfo,
    } = useAppRelease();
    const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
    const [isCheckingUpdates, setIsCheckingUpdates] = useState<boolean>(false);

    const refreshServiceWorkerRegistration = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
            setRegistration(null);
            return null;
        }

        const nextRegistration = await navigator.serviceWorker.getRegistration();
        setRegistration(nextRegistration ?? null);
        return nextRegistration ?? null;
    }, []);

    useEffect(() => {
        refreshServiceWorkerRegistration().catch((error) => {
            console.warn('[Updates] Service worker registratie ophalen mislukt.', error);
        });
    }, [refreshServiceWorkerRegistration]);

    useEffect(() => {
        const handleSwUpdate = (event: Event) => {
            const nextRegistration = (event as CustomEvent<ServiceWorkerRegistration>).detail;
            setRegistration(nextRegistration);
        };

        document.addEventListener('swUpdate', handleSwUpdate);
        return () => document.removeEventListener('swUpdate', handleSwUpdate);
    }, []);

    const handleCheckForUpdates = useCallback(async () => {
        setIsCheckingUpdates(true);

        try {
            const nextRegistration = await refreshServiceWorkerRegistration();
            await nextRegistration?.update();
            await refreshReleaseInfo();
            await refreshServiceWorkerRegistration();
        } finally {
            setIsCheckingUpdates(false);
        }
    }, [refreshReleaseInfo, refreshServiceWorkerRegistration]);

    const handleApplyUpdate = useCallback(async () => {
        const nextRegistration = registration ?? await refreshServiceWorkerRegistration();

        if (!nextRegistration?.waiting) {
            return;
        }

        applyUpdate(nextRegistration);
    }, [refreshServiceWorkerRegistration, registration]);

    const serviceWorkerStatus = useMemo(() => getServiceWorkerStatusLabel(registration), [registration]);
    const canApplyUpdate = Boolean(registration?.waiting);

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonMenuButton/>
                    </IonButtons>
                    <IonTitle>Updates</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding updates-page">
                <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle>Release status</IonCardSubtitle>
                        <IonCardTitle>Frontend versiebeheer</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div className="updates-metric-grid">
                            <div className="updates-metric-card">
                                <IonNote>Geinstalleerd</IonNote>
                                <strong>{installedRelease?.version ?? 'Onbekend'}</strong>
                                <IonText color="medium">{formatReleaseTimestamp(installedRelease?.buildTime)}</IonText>
                            </div>
                            <div className="updates-metric-card">
                                <IonNote>Nieuwste bekend</IonNote>
                                <strong>{latestRelease?.version ?? 'Nog niet opgehaald'}</strong>
                                <IonText color="medium">{formatReleaseTimestamp(latestRelease?.buildTime)}</IonText>
                            </div>
                            <div className="updates-metric-card">
                                <IonNote>Status</IonNote>
                                <strong>{canApplyUpdate || isUpdateAvailable ? 'Update beschikbaar' : 'Up-to-date'}</strong>
                                <IonText color="medium">Laatste check: {formatReleaseTimestamp(lastCheckedAt)}</IonText>
                            </div>
                        </div>
                    </IonCardContent>
                    <IonCardContent className="updates-actions">
                        <IonButton expand="block" onClick={handleCheckForUpdates} disabled={isChecking || isCheckingUpdates}>
                            {isChecking || isCheckingUpdates ? 'Controleren...' : 'Check for updates'}
                        </IonButton>
                        <IonButton expand="block" fill="outline" onClick={handleApplyUpdate} disabled={!canApplyUpdate}>
                            Nu bijwerken
                        </IonButton>
                    </IonCardContent>
                </IonCard>
            </IonContent>
        </IonPage>
    );
};

export default UpdatesPage;